import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, type Papel } from '../auth/AuthContext.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { mensagemDoErro, requisitar } from '../lib/api.js';
import './Usuarios.css';

/**
 * Gestão de usuários — Épico UX, Frente 1, Story 1.5. **Fecha a Frente 1.**
 *
 * Consome a API da 1.3. As regras de quem pode o quê já vivem lá — gestor não
 * administra admin, ninguém se auto-desativa, o tenant não fica sem admin — e
 * esta tela **reflete** cada uma delas, sem reimplementar nenhuma: o servidor
 * recusa de qualquer forma, e o que a UI evita é oferecer um caminho que
 * terminaria em 403.
 *
 * ## Senha não se troca aqui
 *
 * De propósito, e o backend concorda: o `PATCH` recusa o campo `senha` com 400
 * em vez de ignorá-lo. Trocar a senha é self-service (1.4/1.6) — um gestor que
 * pudesse definir a senha de um vendedor teria um jeito silencioso de assumir a
 * conta dele. O que se define aqui é a senha **inicial**, na criação, e o
 * usuário é obrigado a trocá-la no primeiro acesso.
 */

const PAPEIS: readonly Papel[] = ['admin', 'gestor', 'vendedor'];

const ROTULO_PAPEL: Record<Papel, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  vendedor: 'Vendedor',
};

/** Quantos por página. A API devolve tudo; a paginação é da tela — ver o handoff. */
const POR_PAGINA = 20;

interface UsuarioResumo {
  id: string;
  nome: string;
  email: string;
  role: Papel;
  ativo: boolean;
  precisaTrocarSenha: boolean;
}

/**
 * O que **este** usuário pode fazer com **aquela** linha.
 *
 * Espelha as regras da Story 1.3, e a duplicação é consciente: sem ela, a tela
 * ofereceria botões que terminam em 403. A guarda de verdade continua no
 * servidor — se as duas divergirem, quem manda é ele, e o usuário vê a
 * mensagem em vez de um sucesso falso.
 */
function podeEditar(
  atual: { id: string; role: Papel },
  alvo: UsuarioResumo,
  adminsAtivos: number,
): { editar: boolean; desativar: boolean; motivo?: string } {
  // Gestor não administra admin (Story 1.3).
  if (atual.role === 'gestor' && alvo.role === 'admin') {
    return { editar: false, desativar: false, motivo: 'Só um administrador edita outro.' };
  }
  // Ninguém muda o próprio papel nem se desativa.
  if (atual.id === alvo.id) {
    return { editar: false, desativar: false, motivo: 'Você não altera a própria conta aqui.' };
  }
  // O tenant não pode ficar sem admin ativo.
  if (alvo.role === 'admin' && alvo.ativo && adminsAtivos <= 1) {
    return { editar: false, desativar: false, motivo: 'É o último administrador ativo.' };
  }
  return { editar: true, desativar: true };
}

export function Usuarios(): JSX.Element {
  const { usuario } = useAuth();
  const cliente = useQueryClient();

  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(0);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<UsuarioResumo | undefined>(undefined);
  const [recado, setRecado] = useState<string | undefined>(undefined);

  const lista = useQuery({
    queryKey: ['usuarios'],
    queryFn: async () => (await requisitar<UsuarioResumo[]>('/api/usuarios')).dados,
    staleTime: 0,
  });

  const todos = lista.data ?? [];
  const adminsAtivos = todos.filter((u) => u.role === 'admin' && u.ativo).length;

  /*
   * Busca antes de paginar: com número indefinido de vendedores, procurar pelo
   * nome resolve mais rápido do que percorrer páginas — e é o que se faz quando
   * se abre esta tela ("cadê o fulano?").
   */
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (q === '') return todos;
    return todos.filter(
      (u) => u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [todos, busca]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const visiveis = filtrados.slice(paginaAtual * POR_PAGINA, (paginaAtual + 1) * POR_PAGINA);

  function aoTerminar(mensagem: string): void {
    setRecado(mensagem);
    setCriando(false);
    setEditando(undefined);
    void cliente.invalidateQueries({ queryKey: ['usuarios'] });
  }

  return (
    <section className="pagina usuarios">
      <header className="pagina__cabecalho">
        <div>
          <h1>Usuários</h1>
          <p className="pagina__sub">
            Quem entra no sistema e o que cada um pode fazer. A senha é trocada pelo próprio usuário
            no primeiro acesso.
          </p>
        </div>
        <button
          type="button"
          className="botao botao--primario"
          onClick={() => {
            setEditando(undefined);
            setCriando(true);
            setRecado(undefined);
          }}
        >
          Novo usuário
        </button>
      </header>

      {recado !== undefined && (
        <p className="nota nota--ok" role="status">
          {recado}
        </p>
      )}

      {criando && usuario !== undefined && (
        <FormularioCriar
          papelDoAutor={usuario.role}
          aoCancelar={() => {
            setCriando(false);
          }}
          aoCriar={(nome) => {
            aoTerminar(`Usuário ${nome} criado. Ele troca a senha no primeiro acesso.`);
          }}
        />
      )}

      {editando !== undefined && usuario !== undefined && (
        <FormularioEditar
          alvo={editando}
          aoCancelar={() => {
            setEditando(undefined);
          }}
          aoSalvar={(nome) => {
            aoTerminar(`Usuário ${nome} atualizado.`);
          }}
        />
      )}

      <div className="cartao usuarios__filtro">
        <label className="campo usuarios__busca">
          <span className="campo__rotulo">Buscar por nome ou e-mail</span>
          <input
            type="search"
            className="campo__entrada"
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setPagina(0);
            }}
            placeholder="ex.: Maria, maria@empresa.com"
          />
        </label>
        <p className="usuarios__contagem">
          {filtrados.length} {filtrados.length === 1 ? 'usuário' : 'usuários'}
          {busca.trim() !== '' && ` (de ${String(todos.length)})`}
        </p>
      </div>

      {lista.isPending ? (
        <div className="cartao">
          <Carregando rotulo="Carregando usuários…" />
        </div>
      ) : lista.isError ? (
        <div className="cartao">
          <Erro
            erro={lista.error}
            aoRepetir={() => {
              void lista.refetch();
            }}
          />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="cartao">
          <Vazio
            titulo={busca.trim() === '' ? 'Nenhum usuário ainda' : 'Ninguém encontrado'}
            descricao={
              busca.trim() === ''
                ? 'Crie o primeiro acesso para a sua equipe.'
                : `Nenhum nome ou e-mail contém "${busca.trim()}".`
            }
          />
        </div>
      ) : (
        <div className="cartao usuarios__quadro">
          <table className="tabela">
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">E-mail</th>
                <th scope="col">Papel</th>
                <th scope="col">Situação</th>
                <th scope="col">
                  <span className="visualmente-oculto">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((u) => {
                const permissao =
                  usuario === undefined
                    ? { editar: false, desativar: false }
                    : podeEditar({ id: usuario.id, role: usuario.role }, u, adminsAtivos);
                return (
                  <tr key={u.id} className={u.ativo ? undefined : 'usuarios__inativo'}>
                    <td>
                      {u.nome}
                      {usuario?.id === u.id && <span className="usuarios__voce"> (você)</span>}
                    </td>
                    <td className="usuarios__email">{u.email}</td>
                    <td>{ROTULO_PAPEL[u.role]}</td>
                    <td>
                      <span className={u.ativo ? 'etiqueta etiqueta--ok' : 'etiqueta'}>
                        {u.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      {u.precisaTrocarSenha && (
                        <span className="etiqueta etiqueta--aviso usuarios__pendente">
                          senha pendente
                        </span>
                      )}
                    </td>
                    <td className="usuarios__acoes">
                      {permissao.editar ? (
                        <button
                          type="button"
                          className="botao botao--discreto"
                          aria-label={`Editar ${u.nome}`}
                          onClick={() => {
                            setCriando(false);
                            setEditando(u);
                            setRecado(undefined);
                          }}
                        >
                          Editar
                        </button>
                      ) : (
                        /* O motivo fica visível: um botão que some sem
                           explicação vira chamado de suporte. */
                        <span className="usuarios__bloqueado" title={permissao.motivo}>
                          {permissao.motivo}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPaginas > 1 && (
            <div className="usuarios__paginacao">
              <button
                type="button"
                className="botao botao--neutro"
                disabled={paginaAtual === 0}
                onClick={() => {
                  setPagina((p) => Math.max(0, p - 1));
                }}
              >
                Anterior
              </button>
              <span className="usuarios__pagina">
                Página {paginaAtual + 1} de {totalPaginas}
              </span>
              <button
                type="button"
                className="botao botao--neutro"
                disabled={paginaAtual >= totalPaginas - 1}
                onClick={() => {
                  setPagina((p) => p + 1);
                }}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------- criar

function FormularioCriar({
  papelDoAutor,
  aoCriar,
  aoCancelar,
}: {
  papelDoAutor: Papel;
  aoCriar: (nome: string) => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState<Papel>('vendedor');
  const [senha, setSenha] = useState('');

  /*
   * O gestor não vê a opção "Administrador".
   *
   * O backend recusa com 403 de qualquer forma (Story 1.3); esconder aqui evita
   * oferecer um caminho que termina em erro. É a mesma lógica do botão de
   * excluir da F.2: a UI não é a guarda, mas não deve mentir sobre o que dá.
   */
  const papeisOferecidos = papelDoAutor === 'admin' ? PAPEIS : PAPEIS.filter((p) => p !== 'admin');

  const criar = useMutation({
    mutationFn: async () =>
      (
        await requisitar<{ id: string }>('/api/usuarios', {
          metodo: 'POST',
          corpo: { nome: nome.trim(), email: email.trim(), papel, senha },
        })
      ).dados,
    onSuccess: () => {
      aoCriar(nome.trim());
    },
  });

  return (
    <form
      className="cartao usuarios__form"
      onSubmit={(e) => {
        e.preventDefault();
        criar.mutate();
      }}
    >
      <h2>Novo usuário</h2>

      <div className="usuarios__campos">
        <label className="campo">
          <span className="campo__rotulo">Nome</span>
          <input
            className="campo__entrada"
            value={nome}
            required
            minLength={2}
            onChange={(e) => {
              setNome(e.target.value);
            }}
          />
        </label>
        <label className="campo">
          <span className="campo__rotulo">E-mail</span>
          <input
            type="email"
            className="campo__entrada"
            value={email}
            required
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </label>
        <label className="campo">
          <span className="campo__rotulo">Papel</span>
          <select
            className="campo__entrada"
            value={papel}
            onChange={(e) => {
              setPapel(e.target.value as Papel);
            }}
          >
            {papeisOferecidos.map((p) => (
              <option key={p} value={p}>
                {ROTULO_PAPEL[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="campo__rotulo">Senha inicial</span>
          <input
            type="password"
            className="campo__entrada"
            value={senha}
            required
            minLength={12}
            onChange={(e) => {
              setSenha(e.target.value);
            }}
          />
        </label>
        {/*
          A dica fica FORA do <label>.

          Dentro, ela entrava no nome acessível do campo — que passava a ser
          "Senha inicial Mínimo de 12 caracteres…" e deixava de casar com o
          rótulo que a pessoa lê. Leitor de tela e teste liam a mesma coisa
          errada.
        */}
        <p className="campo__dica">Mínimo de 12 caracteres. O usuário troca no primeiro acesso.</p>
      </div>

      {criar.isError && <MensagemDeErro erro={criar.error} />}

      <div className="usuarios__acoes-form">
        <button type="submit" className="botao botao--primario" disabled={criar.isPending}>
          {criar.isPending ? 'Criando…' : 'Criar usuário'}
        </button>
        <button type="button" className="botao botao--neutro" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------ editar

function FormularioEditar({
  alvo,
  aoSalvar,
  aoCancelar,
}: {
  alvo: UsuarioResumo;
  aoSalvar: (nome: string) => void;
  aoCancelar: () => void;
}): JSX.Element {
  const [nome, setNome] = useState(alvo.nome);
  const [papel, setPapel] = useState<Papel>(alvo.role);

  const salvar = useMutation({
    mutationFn: async (corpo: Record<string, unknown>) =>
      (await requisitar(`/api/usuarios/${alvo.id}`, { metodo: 'PATCH', corpo })).dados,
    onSuccess: () => {
      aoSalvar(alvo.nome);
    },
  });

  return (
    <form
      className="cartao usuarios__form"
      onSubmit={(e) => {
        e.preventDefault();
        /*
         * Só o que MUDOU vai no corpo. O `PATCH` recusa `{}` com 400 ("nada
         * para mudar"), e mandar o papel inalterado faria o backend recusar por
         * escalada num caso em que nada estava sendo escalado.
         */
        const corpo: Record<string, unknown> = {};
        if (nome.trim() !== alvo.nome) corpo['nome'] = nome.trim();
        if (papel !== alvo.role) corpo['papel'] = papel;
        salvar.mutate(corpo);
      }}
    >
      <h2>Editar {alvo.nome}</h2>
      <p className="usuarios__email-fixo">
        {alvo.email}
        {/* E-mail não se edita: a API não aceita o campo. Mostrar um input que
            o servidor ignora seria pior do que não mostrar. */}
        <span className="usuarios__dica-inline"> — o e-mail não pode ser alterado</span>
      </p>

      <div className="usuarios__campos">
        <label className="campo">
          <span className="campo__rotulo">Nome</span>
          <input
            className="campo__entrada"
            value={nome}
            required
            minLength={2}
            onChange={(e) => {
              setNome(e.target.value);
            }}
          />
        </label>
        <label className="campo">
          <span className="campo__rotulo">Papel</span>
          <select
            className="campo__entrada"
            value={papel}
            onChange={(e) => {
              setPapel(e.target.value as Papel);
            }}
          >
            {PAPEIS.map((p) => (
              <option key={p} value={p}>
                {ROTULO_PAPEL[p]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {salvar.isError && <MensagemDeErro erro={salvar.error} />}

      <div className="usuarios__acoes-form">
        <button type="submit" className="botao botao--primario" disabled={salvar.isPending}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>

        {/*
          Desativar fica AQUI, dentro da edição, e não como botão na linha da
          tabela: é a ação irreversível do dia a dia (a pessoa perde o acesso),
          e uma lista cheia de botões vermelhos convida ao clique errado.
        */}
        <button
          type="button"
          className={alvo.ativo ? 'botao botao--neutro usuarios__desativar' : 'botao botao--neutro'}
          disabled={salvar.isPending}
          onClick={() => {
            const acao = alvo.ativo ? 'Desativar' : 'Reativar';
            if (
              alvo.ativo &&
              !window.confirm(
                `Desativar ${alvo.nome}? Ele perde o acesso ao sistema imediatamente.`,
              )
            ) {
              return;
            }
            void acao;
            salvar.mutate({ ativo: !alvo.ativo });
          }}
        >
          {alvo.ativo ? 'Desativar acesso' : 'Reativar acesso'}
        </button>

        <button type="button" className="botao botao--neutro" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

/**
 * O erro da API, com o 409 dizendo o que de fato houve.
 *
 * `mensagemDoErro` já traduz por código, mas o e-mail duplicado merece texto
 * próprio: "dados inválidos" mandaria o usuário reler um formulário que está
 * correto — o problema não é o que ele digitou, é que aquela conta já existe.
 */
function MensagemDeErro({ erro }: { erro: unknown }): JSX.Element {
  const conflito =
    typeof erro === 'object' && erro !== null && 'code' in erro
      ? (erro as { code?: string }).code === 'RESOURCE_CONFLICT'
      : false;

  return (
    <p className="aviso" role="alert">
      {conflito ? 'Já existe um usuário com este e-mail.' : mensagemDoErro(erro)}
    </p>
  );
}
